import React, { useState, useEffect, useCallback } from 'react';
import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaUtensils,
  FaLeaf,
  FaFire,
  FaImage,
  FaToggleOn,
  FaToggleOff,
  FaSpinner,
  FaExclamationTriangle,
  FaLayerGroup
} from 'react-icons/fa';
import { getData, putData } from '../../../Services/Fetchnodeservices';

const MenuScreen = ({ designationId: propDesignationId, isDark = false, onRefresh }) => {
  // State
  const [designationId, setDesignationId] = useState(propDesignationId || null);
  const [menu, setMenu] = useState([]);
  const [filteredMenu, setFilteredMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [markupCommission, setMarkupCommission] = useState(20);
  const [profitCommission, setProfitCommission] = useState(20);
  const [updatingStock, setUpdatingStock] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);

  const IMAGE_BASE_URL = process.env.REACT_APP_API_URL || 'https://new.zaptodoor.com/uploads/';

  // Get restaurant ID from localStorage
  const getRestaurantId = useCallback(() => {
    if (designationId) return designationId;
    const storedId = localStorage.getItem('restaurantId');
    if (storedId) return parseInt(storedId);
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        return parsed.id || parsed.restaurantId || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  }, [designationId]);

  // Fetch restaurant details
  const fetchRestaurantDetails = useCallback(async (id) => {
    if (!id) return;
    try {
      const resp = await getData(`restaurant/Restrunts/${id}`);
      if (resp.status && resp.data) {
        const markup = parseFloat(resp.data.commission_calc) ?? parseFloat(resp.data.comission) ?? 20;
        const profit = parseFloat(resp.data.comission) ?? 20;
        setMarkupCommission(markup);
        setProfitCommission(profit);
      } else {
        console.warn('⚠️ No commission data, using defaults 20%');
      }
    } catch (e) {
      console.error('Failed to fetch commission:', e);
    }
  }, []);

  // Fetch menu
  const fetchMenu = useCallback(async () => {
    const id = getRestaurantId();
    if (!id) {
      setError('Restaurant ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setDesignationId(id);
      const result = await getData(`menu/menu/${id}`);
      console.log('Menu Data:', result);

      if (result?.success) {
        const menuData = result.data || [];
        setMenu(menuData);
        setFilteredMenu(menuData);
      } else {
        setError(result?.message || 'Failed to load menu');
        setMenu([]);
        setFilteredMenu([]);
      }
    } catch (error) {
      console.error('Fetch menu error:', error);
      setError(error.message || 'Something went wrong');
      setMenu([]);
      setFilteredMenu([]);
    } finally {
      setLoading(false);
    }
  }, [getRestaurantId]);

  // Initial load
  useEffect(() => {
    const id = getRestaurantId();
    if (id) {
      fetchRestaurantDetails(id);
      fetchMenu();
    } else {
      setLoading(false);
      setError('Please select a restaurant to view menu');
    }
  }, [getRestaurantId, fetchRestaurantDetails, fetchMenu]);

  // Filter menu based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredMenu(menu);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = menu.filter(item =>
        item.name?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
      setFilteredMenu(filtered);
    }
  }, [searchQuery, menu]);

  // Get image URL
  const getImageUri = (pic1) => {
    if (!pic1) return null;
    if (pic1.startsWith('http://') || pic1.startsWith('https://')) {
      return pic1;
    }
    return `${IMAGE_BASE_URL}${pic1}`;
  };

  // Check if veg
  const isVegItem = (item) => {
    if (!item || item.is_veg === undefined) return true;
    const val = item.is_veg;
    return val === 'Yes' || val === 'yes' || val === '1' ||
      val === 'true' || val === true || val === 'Y' || val === 'y';
  };

  // Toggle stock
  const toggleStock = async (productId) => {
    const id = getRestaurantId();
    if (!id) {
      alert('Restaurant ID not found');
      return;
    }

    try {
      setUpdatingStock(productId);
      const result = await putData(`menu/stock/${id}/${productId}`);
      if (result?.success) {
        await fetchMenu();
        if (onRefresh) onRefresh();
      } else {
        alert(result?.message || 'Failed to update stock status');
        await fetchMenu();
      }
    } catch (error) {
      console.error('Toggle stock error:', error);
      alert('Error updating stock status');
      await fetchMenu();
    } finally {
      setUpdatingStock(null);
    }
  };

  // Toggle variant stock
  const toggleVariantStock = async (productId, variantIndex) => {
    const id = getRestaurantId();
    if (!id) {
      alert('Restaurant ID not found');
      return;
    }

    try {
      setUpdatingStock(`${productId}-${variantIndex}`);
      const result = await putData(`menu/variant/stock/${id}/${productId}/${variantIndex}`);
      if (result?.success) {
        await fetchMenu();
        if (onRefresh) onRefresh();
      } else {
        alert(result?.message || 'Failed to update variant stock');
        await fetchMenu();
      }
    } catch (error) {
      console.error('Toggle variant stock error:', error);
      alert('Error updating variant stock');
      await fetchMenu();
    } finally {
      setUpdatingStock(null);
    }
  };

  // Get pricing
  const getPricing = (item) => {
    let menuPrice = parseFloat(item.base_price) || parseFloat(item.requested_price) || 0;
    if (!menuPrice && item.price) {
      menuPrice = Math.round(item.price / 1.2);
    }
    if (!menuPrice) return { menu: 0, customer: 0, profit: 0 };

    const customer = Math.round(menuPrice * (1 + markupCommission / 100));
    const profit = Math.round(customer * (profitCommission / 100));
    return { menu: menuPrice, customer, profit };
  };

  const getVariantPricing = (variant) => {
    let menuPrice = parseFloat(variant.base_price) || parseFloat(variant.actual_price) || 0;
    if (!menuPrice && variant.item_price_variant) {
      menuPrice = Math.round(variant.item_price_variant / 1.2);
    }
    if (!menuPrice) return { menu: 0, customer: 0, profit: 0 };

    const customer = Math.round(menuPrice * (1 + markupCommission / 100));
    const profit = Math.round(customer * (profitCommission / 100));
    return { menu: menuPrice, customer, profit };
  };

  // Toggle expand
  const handleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // 🎨 ORANGE THEME CONFIGURATION (Matching ZaptoDoor Logo)
  const theme = {
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    inputText: isDark ? '#f8fafc' : '#0f172a',
    hoverBg: isDark ? 'rgba(249, 115, 22, 0.1)' : '#ffedd5',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryDark: '#ea580c',
    primaryGradient: isDark 
      ? 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)'
      : 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
    success: '#16a34a',
    danger: '#dc2626',
    warning: '#f59e0b',
    shadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(249, 115, 22, 0.15)',
  };

  // Styles
  const styles = {
    container: {
      padding: '24px',
      maxWidth: '1400px',
      margin: '0 auto',
      fontFamily: "'Inter', 'DM Sans', 'Segoe UI', sans-serif",
      backgroundColor: theme.bg,
      minHeight: '100vh',
      transition: 'all 0.3s ease',
    },
    header: {
      background: theme.primaryGradient,
      borderRadius: '16px',
      padding: '24px 28px',
      marginBottom: '24px',
      boxShadow: theme.shadow,
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.3)'}`,
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    headerLeft: {
      flex: 1,
    },
    headerTitle: {
      fontSize: '24px',
      fontWeight: 800,
      color: '#ffffff',
      margin: 0,
      letterSpacing: '-0.5px',
    },
    headerSubtitle: {
      fontSize: '13px',
      color: 'rgba(255,255,255,0.85)',
      marginTop: '4px',
      fontWeight: 500,
    },
    statBox: {
      background: 'rgba(255,255,255,0.15)',
      borderRadius: '12px',
      padding: '8px 16px',
      textAlign: 'center',
      border: '1px solid rgba(255,255,255,0.2)',
      backdropFilter: 'blur(8px)',
    },
    statNumber: {
      color: '#ffffff',
      fontSize: '22px',
      fontWeight: 800,
    },
    statLabel: {
      color: 'rgba(255,255,255,0.8)',
      fontSize: '10px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    searchBar: {
      display: 'flex',
      alignItems: 'center',
      background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.9)',
      borderRadius: '12px',
      padding: '0 16px',
      height: '48px',
      marginBottom: '12px',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`,
      backdropFilter: 'blur(8px)',
    },
    searchIcon: {
      color: theme.primary,
      fontSize: '18px',
    },
    searchInput: {
      flex: 1,
      marginLeft: '10px',
      background: 'transparent',
      border: 'none',
      outline: 'none',
      color: isDark ? '#ffffff' : '#0f172a',
      fontSize: '14px',
      fontWeight: 500,
    },
    legend: {
      display: 'flex',
      gap: '16px',
      flexWrap: 'wrap',
    },
    legendText: {
      color: 'rgba(255,255,255,0.9)',
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.3px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    // Table Styles
    tableWrapper: {
      overflowX: 'auto',
      borderRadius: '16px',
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.cardBg,
      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(249, 115, 22, 0.08)',
      transition: 'all 0.3s ease',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '14px',
      minWidth: '900px',
    },
    thead: {
      backgroundColor: isDark 
        ? 'linear-gradient(135deg, rgba(234, 88, 12, 0.2) 0%, rgba(249, 115, 22, 0.1) 100%)'
        : 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
      transition: 'background-color 0.3s ease',
    },
    th: {
      padding: '14px 16px',
      textAlign: 'left',
      fontWeight: 700,
      color: isDark ? '#fdba74' : '#c2410c',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: `1.5px solid ${theme.border}`,
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
    },
    td: {
      padding: '12px 16px',
      borderBottom: `1px solid ${theme.border}`,
      color: theme.text,
      transition: 'all 0.3s ease',
      verticalAlign: 'middle',
    },
    tr: {
      transition: 'background-color 0.2s ease',
      cursor: 'pointer',
    },
    trHover: {
      backgroundColor: theme.hoverBg,
    },
    trExpanded: {
      backgroundColor: isDark ? 'rgba(249, 115, 22, 0.08)' : '#fff7ed',
    },
    // Rest of styles
    dishImage: {
      width: '48px',
      height: '48px',
      borderRadius: '10px',
      objectFit: 'cover',
      border: `2px solid ${theme.border}`,
      flexShrink: 0,
    },
    imagePlaceholder: {
      width: '48px',
      height: '48px',
      borderRadius: '10px',
      background: isDark 
        ? 'linear-gradient(135deg, rgba(249, 115, 22, 0.15) 0%, rgba(251, 146, 60, 0.1) 100%)'
        : 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.primary,
      fontSize: '20px',
      border: `1.5px solid ${theme.border}`,
      flexShrink: 0,
    },
    dishInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    dishDetails: {
      display: 'flex',
      flexDirection: 'column',
    },
    dishName: {
      fontWeight: 700,
      color: theme.text,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '14px',
    },
    dishCategory: {
      fontSize: '11px',
      color: theme.textSecondary,
      marginTop: '2px',
    },
    vegIcon: {
      display: 'inline-flex',
      alignItems: 'center',
    },
    mpText: {
      color: theme.textSecondary,
      fontWeight: 600,
      fontSize: '14px',
    },
    cpText: {
      color: theme.primaryDark,
      fontWeight: 700,
      fontSize: '14px',
    },
    profitText: {
      color: theme.warning,
      fontWeight: 700,
      fontSize: '14px',
    },
    statusBadge: (isLive) => ({
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: 700,
      backgroundColor: isLive ? '#dcfce7' : (isDark ? '#334155' : '#f3f4f6'),
      color: isLive ? '#16a34a' : (isDark ? '#94a3b8' : '#6b7280'),
      whiteSpace: 'nowrap',
      border: `1px solid ${isLive ? '#16a34a' : (isDark ? '#475569' : '#d1d5db')}30`,
    }),
    variantCount: {
      fontSize: '12px',
      color: theme.primary,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      cursor: 'pointer',
    },
    expandIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      color: theme.primary,
      transition: 'transform 0.3s ease, color 0.3s ease',
      marginLeft: '4px',
    },
    expandIconRotated: {
      transform: 'rotate(180deg)',
    },
    toggleButton: (isLive) => ({
      padding: '6px 12px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '11px',
      fontWeight: 700,
      cursor: 'pointer',
      background: isLive ? '#dcfce7' : (isDark ? 'rgba(220, 38, 38, 0.15)' : '#fee2e2'),
      color: isLive ? '#16a34a' : '#dc2626',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s ease',
      border: `1px solid ${isLive ? '#16a34a' : '#dc2626'}30`,
    }),
    // Variant sub-row styles
    variantRow: {
      backgroundColor: isDark ? 'rgba(30, 41, 59, 0.6)' : '#fffaf5',
      transition: 'background-color 0.3s ease',
    },
    variantCell: {
      padding: '10px 16px',
      borderBottom: `1px solid ${theme.border}`,
      color: theme.textSecondary,
      fontSize: '13px',
      transition: 'all 0.3s ease',
    },
    variantName: {
      color: theme.text,
      fontWeight: 600,
      paddingLeft: '30px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    variantPrice: {
      fontSize: '12px',
    },
    variantProfit: {
      color: theme.warning,
      fontWeight: 700,
      fontSize: '12px',
    },
    variantToggle: (isLive) => ({
      padding: '4px 10px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '10px',
      fontWeight: 700,
      cursor: 'pointer',
      background: isLive ? '#dcfce7' : (isDark ? 'rgba(220, 38, 38, 0.15)' : '#fee2e2'),
      color: isLive ? '#16a34a' : '#dc2626',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      transition: 'all 0.2s ease',
      border: `1px solid ${isLive ? '#16a34a' : '#dc2626'}30`,
    }),
    variantStatus: (isLive) => ({
      fontSize: '10px',
      fontWeight: 700,
      color: isLive ? '#16a34a' : '#dc2626',
    }),
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      gap: '16px',
    },
    loadingSpinner: {
      animation: 'spin 1s linear infinite',
      fontSize: '40px',
      background: theme.primaryGradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: theme.textSecondary,
      fontSize: '16px',
    },
    errorContainer: {
      textAlign: 'center',
      padding: '60px 20px',
    },
    errorIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      color: theme.primary,
    },
    footerStats: {
      marginTop: '20px',
      padding: '14px 24px',
      background: theme.cardBg,
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(249, 115, 22, 0.08)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '12px',
    },
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div style={styles.loadingContainer}>
          <FaSpinner style={styles.loadingSpinner} />
          <div style={{ color: theme.textSecondary, fontWeight: 500 }}>Loading menu...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <FaExclamationTriangle style={styles.errorIcon} />
          <h3 style={{ color: theme.text, marginBottom: '8px' }}>Failed to load menu</h3>
          <p style={{ color: theme.textSecondary }}>{error}</p>
          <button
            onClick={fetchMenu}
            style={{
              marginTop: '16px',
              padding: '10px 24px',
              background: theme.primaryGradient,
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .menu-row:hover {
          background-color: ${theme.hoverBg};
        }
        .variant-row:hover {
          background-color: ${isDark ? 'rgba(251, 146, 60, 0.15)' : '#ffedd5'};
        }
        .dish-image {
          transition: transform 0.2s ease;
        }
        .dish-image:hover {
          transform: scale(1.5);
          z-index: 10;
        }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerLeft}>
            <h1 style={styles.headerTitle}>Menu Master</h1>
            <p style={styles.headerSubtitle}>Manage your digital kitchen</p>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statNumber}>{menu.length}</div>
            <div style={styles.statLabel}>Total Items</div>
          </div>
        </div>

        {/* Search Bar */}
        <div style={styles.searchBar}>
          <FaSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search dishes, categories..."
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Legend */}
        <div style={styles.legend}>
          <span style={styles.legendText}>• <strong style={{color: '#ffffff'}}>MP:</strong> Menu Price</span>
          <span style={styles.legendText}>• <strong style={{color: '#ffffff'}}>CP:</strong> Customer Price</span>
          <span style={styles.legendText}>• <strong style={{color: '#ffffff'}}>Profit:</strong> Platform Commission</span>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <th style={styles.th}>Dish</th>
              <th style={styles.th}>Menu Price (MP)</th>
              <th style={styles.th}>Customer Price (CP)</th>
              <th style={styles.th}>Profit</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Variants</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMenu.length === 0 ? (
              <tr>
                <td colSpan="7" style={styles.emptyState}>
                  {searchQuery ? 'No dishes found matching your search' : 'No items in menu yet'}
                </td>
              </tr>
            ) : (
              filteredMenu.map((item) => {
                const isExpanded = expandedId === item.product_id;
                const imageUri = getImageUri(item.pic1);
                const imageFailed = imageErrors[item.product_id];
                const showImage = imageUri && !imageFailed;
                const isVeg = isVegItem(item);
                const isLive = item.status === '1';
                const { menu: menuPrice, customer, profit } = getPricing(item);
                const variants = item.variant || [];
                const isHovered = hoveredRow === item.product_id;

                return (
                  <React.Fragment key={item.product_id}>
                    {/* Main dish row */}
                    <tr
                      className="menu-row"
                      style={{
                        ...styles.tr,
                        ...(isHovered ? styles.trHover : {}),
                        ...(isExpanded ? styles.trExpanded : {}),
                      }}
                      onMouseEnter={() => setHoveredRow(item.product_id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <td style={styles.td}>
                        <div style={styles.dishInfo}>
                          {showImage ? (
                            <img
                              src={imageUri}
                              alt={item.name}
                              style={styles.dishImage}
                              className="dish-image"
                              onError={() => setImageErrors(prev => ({ ...prev, [item.product_id]: true }))}
                            />
                          ) : (
                            <div style={styles.imagePlaceholder}>
                              <FaImage />
                            </div>
                          )}
                          <div style={styles.dishDetails}>
                            <div style={styles.dishName}>
                              <span style={styles.vegIcon}>
                                {isVeg ? <FaLeaf color="#16a34a" size={14} /> : <FaFire color="#dc2626" size={14} />}
                              </span>
                              {item.name}
                              {item.category && (
                                <span style={{
                                  fontSize: '10px',
                                  background: isDark ? 'rgba(249, 115, 22, 0.15)' : '#fff7ed',
                                  color: theme.primaryDark,
                                  padding: '2px 8px',
                                  borderRadius: '8px',
                                  fontWeight: 700,
                                  border: `1px solid ${theme.border}`,
                                }}>
                                  {item.category}
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <div style={styles.dishCategory}>
                                {item.description.length > 40 ? item.description.substring(0, 40) + '...' : item.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.mpText}>₹{menuPrice.toFixed(2)}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.cpText}>₹{customer.toFixed(2)}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.profitText}>+₹{profit.toFixed(2)}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.statusBadge(isLive)}>
                          {isLive ? '● LIVE' : '● HIDDEN'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div
                          style={styles.variantCount}
                          onClick={() => variants.length > 0 && handleExpand(item.product_id)}
                        >
                          <span>{variants.length} variant{variants.length !== 1 ? 's' : ''}</span>
                          {variants.length > 0 && (
                            <span style={{
                              ...styles.expandIcon,
                              ...(isExpanded ? styles.expandIconRotated : {}),
                            }}>
                              <FaChevronDown />
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <button
                          style={styles.toggleButton(isLive)}
                          onClick={() => toggleStock(item.product_id)}
                          disabled={updatingStock === item.product_id}
                        >
                          {updatingStock === item.product_id ? (
                            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={11} />
                          ) : isLive ? (
                            <FaToggleOn />
                          ) : (
                            <FaToggleOff />
                          )}
                          {isLive ? 'Live' : 'Hidden'}
                        </button>
                      </td>
                    </tr>

                    {/* Variant rows - expanded */}
                    {isExpanded && variants.length > 0 && (
                      <>
                        {variants.map((variant, vIndex) => {
                          const { menu: vMenu, customer: vCust, profit: vProfit } = getVariantPricing(variant);
                          const isVariantLive = variant.status === '1';

                          return (
                            <tr
                              key={vIndex}
                              className="variant-row"
                              style={{
                                ...styles.variantRow,
                                ...(vIndex === variants.length - 1 ? { borderBottom: 'none' } : {}),
                              }}
                            >
                              <td style={{
                                ...styles.variantCell,
                                ...styles.variantName,
                              }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '12px', opacity: 0.5, color: theme.primary }}>└</span>
                                  <span>{variant.unit_value || variant.unit || 'Variant'}</span>
                                  <span style={styles.variantStatus(isVariantLive)}>
                                    {isVariantLive ? '● Available' : '● Unavailable'}
                                  </span>
                                </span>
                              </td>
                              <td style={styles.variantCell}>
                                <span style={styles.mpText}>₹{vMenu.toFixed(2)}</span>
                              </td>
                              <td style={styles.variantCell}>
                                <span style={styles.cpText}>₹{vCust.toFixed(2)}</span>
                              </td>
                              <td style={styles.variantCell}>
                                <span style={styles.variantProfit}>+₹{vProfit.toFixed(2)}</span>
                              </td>
                              <td style={styles.variantCell}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '3px 10px',
                                  borderRadius: '10px',
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  backgroundColor: isVariantLive ? '#dcfce7' : (isDark ? 'rgba(220, 38, 38, 0.15)' : '#fee2e2'),
                                  color: isVariantLive ? '#16a34a' : '#dc2626',
                                  border: `1px solid ${isVariantLive ? '#16a34a' : '#dc2626'}30`
                                }}>
                                  {isVariantLive ? 'Available' : 'Unavailable'}
                                </span>
                              </td>
                              <td style={styles.variantCell}>
                                <span style={{ fontSize: '11px', color: theme.textSecondary }}>
                                  {variant.unit || variant.unit_value || ''}
                                </span>
                              </td>
                              <td style={styles.variantCell}>
                                <button
                                  style={styles.variantToggle(isVariantLive)}
                                  onClick={() => toggleVariantStock(item.product_id, vIndex)}
                                  disabled={updatingStock === `${item.product_id}-${vIndex}`}
                                >
                                  {updatingStock === `${item.product_id}-${vIndex}` ? (
                                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={10} />
                                  ) : isVariantLive ? (
                                    <FaToggleOn />
                                  ) : (
                                    <FaToggleOff />
                                  )}
                                  {isVariantLive ? 'In Stock' : 'Out'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Stats */}
      <div style={styles.footerStats}>
        <span style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: 500 }}>
          Total Items: <strong style={{ color: theme.primary, fontSize: '15px' }}>{filteredMenu.length}</strong>
        </span>
        <span style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: 500 }}>
          Total Variants: <strong style={{ color: theme.primary, fontSize: '15px' }}>
            {filteredMenu.reduce((sum, item) => sum + (item.variant?.length || 0), 0)}
          </strong>
        </span>
        <span style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: 500 }}>
          <span style={{ color: '#16a34a', fontWeight: 700 }}>● Live: {filteredMenu.filter(i => i.status === '1').length}</span>
          {' '}
          <span style={{ color: '#6b7280', fontWeight: 700 }}>● Hidden: {filteredMenu.filter(i => i.status !== '1').length}</span>
        </span>
        <span style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: 500 }}>
          <FaLeaf color="#16a34a" size={12} /> Veg: {filteredMenu.filter(i => isVegItem(i)).length}
          {' '}
          <FaFire color="#dc2626" size={12} /> Non-Veg: {filteredMenu.filter(i => !isVegItem(i)).length}
        </span>
      </div>
    </div>
  );
};

export default MenuScreen;